import React, { useState } from 'react';
import logger from '../../utils/logger';
import { useMailQuickContacts } from './useMailQuickContacts';

// Sous-hook Messagerie : liaison d'emails au CRM, annuaire de contacts,
// sélecteur de destinataires et création rapide (client/courtier/annuaire).
export function useMailLinks({
  ITEMS_PER_PAGE,
  authToken,
  backendUrl,
  composeForm,
  emails,
  loadEmails,
  selectedEmail,
  setComposeForm,
  setEmails,
  setSelectedEmail,
  setUnknownEmails,
  showError,
  showSuccess
}) {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showEmailLinkManager, setShowEmailLinkManager] = useState(false);
  const [emailToLink, setEmailToLink] = useState(null);
  const [linkSuggestions, setLinkSuggestions] = useState([]);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactPickerField, setContactPickerField] = useState('to'); // 'to', 'cc', 'bcc'
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactResults, setContactResults] = useState({ clients: [], courtiers: [], companies: [], assisteurs: [], assistance_clients: [] });
  const [activeContactTab, setActiveContactTab] = useState('clients');
  const [showCourtierClients, setShowCourtierClients] = useState(false);
  const [selectedCourtier, setSelectedCourtier] = useState(null);
  const [courtierClients, setCourtierClients] = useState([]);
  const [showCompanyContacts, setShowCompanyContacts] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState({
    clients: 1,
    courtiers: 1,
    companies: 1,
    courtierClients: 1,
    companyContacts: 1,
    assisteurs: 1,
    assistance_clients: 1
  });
  const [knownEmailsInfo, setKnownEmailsInfo] = useState({});

  // Création rapide de contact inconnu (client/courtier/annuaire) — sous-hook dédié
  const {
    showUnknownContactModal, setShowUnknownContactModal,
    unknownContactEmail, setUnknownContactEmail,
    unknownContactStep, setUnknownContactStep,
    unknownContactType, setUnknownContactType,
    creatingContact, setCreatingContact,
    quickClientForm, setQuickClientForm,
    quickCourtierForm, setQuickCourtierForm,
    quickAnnuaireForm, setQuickAnnuaireForm,
    companiesList, setCompaniesList,
    loadCompanies, openUnknownContactModal,
    createQuickClient, createQuickCourtier, createQuickAnnuaireContact
  } = useMailQuickContacts({ backendUrl, authToken, loadEmails, setUnknownEmails, showSuccess, showError });


  const openLinkModal = async () => {
    if (!selectedEmail) return;
    
    try {
      // Charger les suggestions automatiques
      const response = await fetch(`${backendUrl}/api/admin/mail/email/${selectedEmail.id}/suggestions`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLinkSuggestions(data.suggestions || []);
        setSelectedLinks(data.current_links || []);
      }
    } catch (error) {
      logger.error('Error loading suggestions:', error);
    }
    
    setLinkSearchQuery('');
    setLinkSearchResults([]);
    setShowLinkModal(true);
  };

  // Rechercher des entités pour liaison
  const searchEntitiesForLink = async (query) => {
    if (query.length < 2) {
      setLinkSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/search-entities?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLinkSearchResults(data.results || []);
      }
    } catch (error) {
      logger.error('Error searching entities:', error);
    }
  };

  // Ajouter une entité aux liaisons sélectionnées
  const addToSelectedLinks = (entity) => {
    if (!selectedLinks.find(l => l.type === entity.type && l.id === entity.id)) {
      setSelectedLinks([...selectedLinks, entity]);
    }
    setLinkSearchQuery('');
    setLinkSearchResults([]);
  };

  // Retirer une entité des liaisons
  const removeFromSelectedLinks = (entity) => {
    setSelectedLinks(selectedLinks.filter(l => !(l.type === entity.type && l.id === entity.id)));
  };

  // Sauvegarder les liaisons
  const saveLinkAndProcess = async (processAfter = false) => {
    if (!selectedEmail) return;
    
    try {
      // Sauvegarder les liaisons
      const linkResponse = await fetch(`${backendUrl}/api/admin/mail/email/${selectedEmail.id}/link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedLinks.map(l => ({ type: l.type, id: l.id })))
      });
      
      if (!linkResponse.ok) {
        throw new Error('Erreur lors de la liaison');
      }
      
      // Marquer comme traité si demandé
      if (processAfter) {
        const processResponse = await fetch(`${backendUrl}/api/admin/mail/email/${selectedEmail.id}/process`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (processResponse.ok) {
          showSuccess('Email lié et traité avec succès');
          setEmails(emails.filter(e => e.id !== selectedEmail.id));
          setSelectedEmail(null);
        }
      } else {
        showSuccess('Liaisons enregistrées');

        // Construire les badges CRM localement pour une mise à jour immédiate sans refresh
        const newBadges = selectedLinks.map(l => ({
          type: l.type,
          id: l.id,
          label: l.name || l.label || l.admin_reference || l.id,
          color: l.color || null,
          admin_reference: l.admin_reference || null
        }));

        // Mettre à jour l'email dans la liste ET l'email sélectionné
        setEmails(prev => prev.map(e =>
          e.id === selectedEmail.id
            ? { ...e, linked_entities: selectedLinks, crm_badges: newBadges }
            : e
        ));
        setSelectedEmail(prev => ({
          ...prev,
          linked_entities: selectedLinks,
          crm_badges: newBadges
        }));

        // Refresh silencieux en arrière-plan pour récupérer les crm_badges complets du serveur
        if (loadEmails) setTimeout(() => loadEmails(), 800);
      }
      
      setShowLinkModal(false);
    } catch (error) {
      logger.error('Error saving links:', error);
      showError('Erreur lors de l\'enregistrement');
    }
  };

  const openContactPicker = (field) => {
    setContactPickerField(field);
    setShowContactPicker(true);
    setContactSearchQuery('');
    searchContacts('');
  };

  const searchContacts = async (query) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/contact-directory?query=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const results = await response.json();
        setContactResults(results);
      }
    } catch (error) {
      logger.error('Error searching contacts:', error);
    }
  };

  const selectContactEmail = (email) => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    // Si l'email est déjà présent, le retirer (toggle)
    if (existingEmails.includes(email)) {
      const remainingEmails = existingEmails.filter(e => e !== email);
      const newValue = remainingEmails.join(', ');
      
      setComposeForm({
        ...composeForm,
        [contactPickerField]: newValue
      });
      
      showSuccess(`✓ Email retiré`);
      return;
    }
    
    // Sinon, l'ajouter
    const newValue = currentValue ? `${currentValue}, ${email}` : email;
    
    setComposeForm({
      ...composeForm,
      [contactPickerField]: newValue
    });
    
    showSuccess(`✓ Email ajouté`);
  };

  const isEmailSelected = (email) => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    return existingEmails.includes(email);
  };

  const getSelectedCountByCategory = () => {
    const currentValue = composeForm[contactPickerField];
    const selectedEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    // Emails de chaque catégorie
    const clientEmails = contactResults.clients.flatMap(c => c.emails);
    const courtierEmails = contactResults.courtiers.flatMap(c => c.emails);
    const companyEmails = contactResults.companies.flatMap(c => c.emails);
    const assisteurEmails = (contactResults.assisteurs || []).flatMap(c => c.emails);
    const assistanceClientEmails = (contactResults.assistance_clients || []).flatMap(c => c.emails);
    
    // Compter combien sont sélectionnés dans chaque catégorie
    const clientsCount = selectedEmails.filter(email => clientEmails.includes(email)).length;
    const courtiersCount = selectedEmails.filter(email => courtierEmails.includes(email)).length;
    const companiesCount = selectedEmails.filter(email => companyEmails.includes(email)).length;
    const assisteursCount = selectedEmails.filter(email => assisteurEmails.includes(email)).length;
    const assistanceClientsCount = selectedEmails.filter(email => assistanceClientEmails.includes(email)).length;
    
    return {
      clients: clientsCount,
      courtiers: courtiersCount,
      companies: companiesCount,
      assisteurs: assisteursCount,
      assistance_clients: assistanceClientsCount,
      total: selectedEmails.length
    };
  };

  const openCourtierClients = async (courtier) => {
    setSelectedCourtier(courtier);
    setShowCourtierClients(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/contact-directory/courtier/${courtier.id}/clients`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCourtierClients(data.clients);
      }
    } catch (error) {
      logger.error('Error loading courtier clients:', error);
      showError('Erreur lors du chargement des clients');
    }
  };

  const selectAllCourtierClients = () => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    const allEmails = courtierClients.flatMap(c => c.emails);
    const uniqueEmails = [...new Set([...existingEmails, ...allEmails])];
    const newValue = uniqueEmails.join(', ');
    
    setComposeForm({
      ...composeForm,
      [contactPickerField]: newValue
    });
    
    const addedCount = uniqueEmails.length - existingEmails.length;
    showSuccess(`✓ ${addedCount} client(s) du courtier ajouté(s)`);
  };

  const deselectAllCourtierClients = () => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    const allEmailsInCourtier = courtierClients.flatMap(c => c.emails);
    const remainingEmails = existingEmails.filter(email => !allEmailsInCourtier.includes(email));
    const newValue = remainingEmails.join(', ');
    
    setComposeForm({
      ...composeForm,
      [contactPickerField]: newValue
    });
    
    const removedCount = existingEmails.length - remainingEmails.length;
    showSuccess(`✓ ${removedCount} client(s) du courtier retiré(s)`);
  };

  const openCompanyContacts = (company) => {
    setSelectedCompany(company);
    setShowCompanyContacts(true);
    
    // Extraire tous les contacts de la compagnie
    const contacts = contactResults.companies.filter(c => 
      c.company_name === company.name
    );
    setCompanyContacts(contacts);
  };

  const selectAllCompanyContacts = () => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    const allEmails = companyContacts.flatMap(c => c.emails);
    const uniqueEmails = [...new Set([...existingEmails, ...allEmails])];
    const newValue = uniqueEmails.join(', ');
    
    setComposeForm({
      ...composeForm,
      [contactPickerField]: newValue
    });
    
    const addedCount = uniqueEmails.length - existingEmails.length;
    showSuccess(`✓ ${addedCount} contact(s) de la compagnie ajouté(s)`);
  };

  const deselectAllCompanyContacts = () => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    const allEmailsInCompany = companyContacts.flatMap(c => c.emails);
    const remainingEmails = existingEmails.filter(email => !allEmailsInCompany.includes(email));
    const newValue = remainingEmails.join(', ');
    
    setComposeForm({
      ...composeForm,
      [contactPickerField]: newValue
    });
    
    const removedCount = existingEmails.length - remainingEmails.length;
    showSuccess(`✓ ${removedCount} contact(s) de la compagnie retiré(s)`);
  };

  const getUniqueCompanies = () => {
    // Regrouper les contacts par compagnie
    const companiesMap = new Map();
    
    contactResults.companies.forEach(contact => {
      const companyName = contact.company_name;
      if (!companiesMap.has(companyName)) {
        companiesMap.set(companyName, {
          name: companyName,
          contactCount: 0,
          totalEmails: 0
        });
      }
      const company = companiesMap.get(companyName);
      company.contactCount += 1;
      company.totalEmails += contact.emails.length;
    });
    
    return Array.from(companiesMap.values());
  };

  const paginateItems = (items, page) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (itemsCount) => {
    return Math.ceil(itemsCount / ITEMS_PER_PAGE);
  };

  const changePage = (category, newPage) => {
    setCurrentPage({
      ...currentPage,
      [category]: newPage
    });
  };

  const renderPagination = (category, totalItems) => {
    const totalPages = getTotalPages(totalItems);
    if (totalPages <= 1) return null;

    const currentPageNum = currentPage[category];

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200">
        <p className="text-sm text-slate-600">
          Page {currentPageNum} sur {totalPages} ({totalItems} éléments)
        </p>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => changePage(category, currentPageNum - 1)}
            disabled={currentPageNum === 1}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              currentPageNum === 1
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
            }`}
          >
            Précédent
          </button>
          
          {/* Numéros de pages */}
          <div className="flex items-center space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
              // Afficher seulement quelques pages autour de la page actuelle
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPageNum - 1 && pageNum <= currentPageNum + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => changePage(category, pageNum)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      pageNum === currentPageNum
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (
                pageNum === currentPageNum - 2 ||
                pageNum === currentPageNum + 2
              ) {
                return <span key={pageNum} className="text-slate-400">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => changePage(category, currentPageNum + 1)}
            disabled={currentPageNum === totalPages}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              currentPageNum === totalPages
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
            }`}
          >
            Suivant
          </button>
        </div>
      </div>
    );
  };

  const selectAllFromTab = () => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    // Récupérer tous les emails de l'onglet actif
    let allEmailsInTab = [];
    if (activeContactTab === 'clients') {
      allEmailsInTab = contactResults.clients.flatMap(c => c.emails);
    } else if (activeContactTab === 'courtiers') {
      allEmailsInTab = contactResults.courtiers.flatMap(c => c.emails);
    } else if (activeContactTab === 'companies') {
      allEmailsInTab = contactResults.companies.flatMap(c => c.emails);
    } else if (activeContactTab === 'assisteurs') {
      allEmailsInTab = (contactResults.assisteurs || []).flatMap(c => c.emails);
    } else if (activeContactTab === 'assistance_clients') {
      allEmailsInTab = (contactResults.assistance_clients || []).flatMap(c => c.emails);
    }
    
    // Fusionner avec les emails existants (sans doublons)
    const uniqueEmails = [...new Set([...existingEmails, ...allEmailsInTab])];
    const newValue = uniqueEmails.join(', ');
    
    setComposeForm({
      ...composeForm,
      [contactPickerField]: newValue
    });
    
    const addedCount = uniqueEmails.length - existingEmails.length;
    showSuccess(`✓ ${addedCount} email(s) ajouté(s)`);
  };

  const deselectAllFromTab = () => {
    const currentValue = composeForm[contactPickerField];
    const existingEmails = currentValue ? currentValue.split(',').map(e => e.trim()) : [];
    
    // Récupérer tous les emails de l'onglet actif
    let allEmailsInTab = [];
    if (activeContactTab === 'clients') {
      allEmailsInTab = contactResults.clients.flatMap(c => c.emails);
    } else if (activeContactTab === 'courtiers') {
      allEmailsInTab = contactResults.courtiers.flatMap(c => c.emails);
    } else if (activeContactTab === 'companies') {
      allEmailsInTab = contactResults.companies.flatMap(c => c.emails);
    } else if (activeContactTab === 'assisteurs') {
      allEmailsInTab = (contactResults.assisteurs || []).flatMap(c => c.emails);
    } else if (activeContactTab === 'assistance_clients') {
      allEmailsInTab = (contactResults.assistance_clients || []).flatMap(c => c.emails);
    }
    
    // Retirer les emails de l'onglet actif
    const remainingEmails = existingEmails.filter(email => !allEmailsInTab.includes(email));
    const newValue = remainingEmails.join(', ');
    
    setComposeForm({
      ...composeForm,
      [contactPickerField]: newValue
    });
    
    const removedCount = existingEmails.length - remainingEmails.length;
    showSuccess(`✓ ${removedCount} email(s) retiré(s)`);
  };

  return {
    // États
    showLinkModal, setShowLinkModal,
    showEmailLinkManager, setShowEmailLinkManager,
    emailToLink, setEmailToLink,
    linkSuggestions, setLinkSuggestions,
    linkSearchQuery, setLinkSearchQuery,
    linkSearchResults, setLinkSearchResults,
    selectedLinks, setSelectedLinks,
    showContactPicker, setShowContactPicker,
    contactPickerField, setContactPickerField,
    contactSearchQuery, setContactSearchQuery,
    contactResults, setContactResults,
    activeContactTab, setActiveContactTab,
    showCourtierClients, setShowCourtierClients,
    selectedCourtier, setSelectedCourtier,
    courtierClients, setCourtierClients,
    showCompanyContacts, setShowCompanyContacts,
    selectedCompany, setSelectedCompany,
    companyContacts, setCompanyContacts,
    currentPage, setCurrentPage,
    knownEmailsInfo, setKnownEmailsInfo,
    showUnknownContactModal, setShowUnknownContactModal,
    unknownContactEmail, setUnknownContactEmail,
    unknownContactStep, setUnknownContactStep,
    unknownContactType, setUnknownContactType,
    creatingContact, setCreatingContact,
    quickClientForm, setQuickClientForm,
    quickCourtierForm, setQuickCourtierForm,
    quickAnnuaireForm, setQuickAnnuaireForm,
    companiesList, setCompaniesList,
    // Fonctions
    loadCompanies, openUnknownContactModal,
    createQuickClient, createQuickCourtier, createQuickAnnuaireContact,
    openLinkModal, searchEntitiesForLink, addToSelectedLinks, removeFromSelectedLinks, saveLinkAndProcess,
    openContactPicker, searchContacts, selectContactEmail, isEmailSelected, getSelectedCountByCategory,
    openCourtierClients, selectAllCourtierClients, deselectAllCourtierClients,
    openCompanyContacts, selectAllCompanyContacts, deselectAllCompanyContacts,
    getUniqueCompanies, paginateItems, getTotalPages, changePage, renderPagination,
    selectAllFromTab, deselectAllFromTab
  };
}
